import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

import { TextField, TextareaField, SelectField } from "./Fields";
import Honeypot from "./Honeypot";
import Turnstile from "./Turnstile";
import { quoteSchema, SERVICE_TYPES, PICKUP_TIMES } from "../../lib/schemas";
import { submitQuoteLead } from "../../lib/api";
import { trackEvent } from "../../lib/analytics";

const SUMMARY_LABELS = {
  service_type: "Service",
  pickup_city: "Pickup",
  dropoff_city: "Drop-off",
  pickup_date: "Pickup Date",
  pickup_time: "Pickup Time",
  weight_kg: "Weight (kg)",
  cargo_description: "Cargo",
  special_requirements: "Notes",
  name: "Name",
  company: "Company",
  email: "Email",
  phone: "Phone",
};

export default function QuoteForm() {
  const [serverError, setServerError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(quoteSchema),
    defaultValues: { _hp_check: "", subject: "General Inquiry" },
    shouldFocusError: true,
  });

  const onSubmit = async (values) => {
    setServerError(null);
    const res = await submitQuoteLead({
      ...values,
      turnstile_token: turnstileToken || undefined,
    });
    if (!res.ok) {
      setServerError(res.message || "Something went wrong. Please try again.");
      return;
    }
    trackEvent("generate_lead", {
      lead_type: "quote",
      service_type: values.service_type,
      value: 0,
    });
    setSuccess({ ...values, lead_id: res.lead_id });
    reset();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (success) {
    return (
      <div
        className="rounded-md border border-ridge-border bg-white p-8 md:p-10"
        role="status"
        aria-live="polite"
        data-testid="quote-success"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-ridge-amber/15 text-ridge-amber">
          <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
        </div>
        <h3 className="font-display mt-5 text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">
          Quote request received.
        </h3>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-ridge-text md:text-base">
          Thanks, <strong>{success.name}</strong>. We've received your request and will respond
          within one business hour during operating hours (Mon–Fri 7am–7pm EST). A
          confirmation email is on its way to <strong>{success.email}</strong>.
        </p>
        <div className="mt-7 rounded-md border border-ridge-border bg-ridge-stone p-5 md:p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ridge-amber">
            What you submitted
          </p>
          <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
            {Object.entries(SUMMARY_LABELS).map(([key, label]) => {
              const v = success[key];
              if (!v) return null;
              return (
                <div key={key} className="text-sm">
                  <dt className="text-ridge-muted">{label}</dt>
                  <dd className="font-medium text-ridge-charcoal">{String(v)}</dd>
                </div>
              );
            })}
          </dl>
        </div>
        <button
          type="button"
          onClick={() => setSuccess(null)}
          className="btn-ghost-amber mt-7"
          data-testid="quote-submit-another"
        >
          Submit another request
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="rounded-md border border-ridge-border bg-white p-6 md:p-8"
      aria-label="Detailed quote request"
      data-testid="quote-form"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <TextField
          name="name"
          label="Name"
          required
          autoComplete="name"
          register={register}
          error={errors.name?.message}
          testId="quote-name"
        />
        <TextField
          name="company"
          label="Company"
          hint="Optional"
          autoComplete="organization"
          register={register}
          error={errors.company?.message}
          testId="quote-company"
        />
        <TextField
          name="email"
          label="Email"
          type="email"
          required
          autoComplete="email"
          register={register}
          error={errors.email?.message}
          testId="quote-email"
        />
        <TextField
          name="phone"
          label="Phone"
          type="tel"
          hint="Optional"
          autoComplete="tel"
          register={register}
          error={errors.phone?.message}
          testId="quote-phone"
        />
        <div className="sm:col-span-2">
          <SelectField
            name="service_type"
            label="Service Type"
            required
            options={SERVICE_TYPES}
            register={register}
            error={errors.service_type?.message}
            testId="quote-service-type"
          />
        </div>
        <TextField
          name="pickup_city"
          label="Pickup City"
          required
          autoComplete="off"
          placeholder="e.g., Milton"
          register={register}
          error={errors.pickup_city?.message}
          testId="quote-pickup-city"
        />
        <TextField
          name="dropoff_city"
          label="Drop-off City"
          required
          autoComplete="off"
          placeholder="e.g., Toronto"
          register={register}
          error={errors.dropoff_city?.message}
          testId="quote-dropoff-city"
        />
        <TextField
          name="pickup_date"
          label="Preferred Pickup Date"
          type="date"
          hint="Optional"
          register={register}
          error={errors.pickup_date?.message}
          testId="quote-pickup-date"
        />
        <SelectField
          name="pickup_time"
          label="Preferred Pickup Time"
          options={PICKUP_TIMES}
          hint="Optional"
          register={register}
          error={errors.pickup_time?.message}
          testId="quote-pickup-time"
        />
        <TextField
          name="weight_kg"
          label="Approx. Weight (kg)"
          type="number"
          inputMode="decimal"
          step="0.1"
          min="0"
          hint="Optional"
          register={register}
          error={errors.weight_kg?.message}
          testId="quote-weight"
        />
        <div className="sm:col-span-2">
          <TextareaField
            name="cargo_description"
            label="Cargo Description"
            placeholder="Describe what you're shipping (optional)"
            hint="Optional · 500 character max"
            maxLength={500}
            rows={3}
            register={register}
            error={errors.cargo_description?.message}
            testId="quote-cargo"
          />
        </div>
        <div className="sm:col-span-2">
          <TextareaField
            name="special_requirements"
            label="Special Requirements"
            placeholder="Temperature controlled, fragile, signature required, etc."
            hint="Optional · 500 character max"
            maxLength={500}
            rows={3}
            register={register}
            error={errors.special_requirements?.message}
            testId="quote-special"
          />
        </div>
      </div>

      <Honeypot register={register} />
      <Turnstile onToken={setTurnstileToken} />

      {serverError ? (
        <p
          className="mt-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
          role="alert"
          data-testid="quote-server-error"
        >
          {serverError}
        </p>
      ) : null}

      <div className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-ridge-muted">
          Required fields marked with <span className="text-ridge-amber">*</span>. We
          respond within one business hour during operating hours.
        </p>
        <button
          type="submit"
          className="btn-accent w-full sm:w-auto"
          disabled={isSubmitting}
          data-testid="quote-submit"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Sending…
            </>
          ) : (
            <>
              Request Quote
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
